import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect
} from "react";

import { useCrossFlow } from "@atlassiansox/cross-flow-support";

import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { TouchpointContextType, TouchpointOptions } from "./types";
import {
    WAC_GENERIC_TOUCHPOINT_NAME,
    getProductCohorts,
    useFireEvent
} from "./utils";

export const initialContext: TouchpointContextType = {
    isCrossFlowEnabled: false,
    productKeys: []
};

export const CrossFlowTouchpointContext =
    createContext<TouchpointContextType>(initialContext);

export const CrossFlowTouchpointDisabled = ({
    children
}: PropsWithChildren) => {
    return (
        <CrossFlowTouchpointContext.Provider value={initialContext}>
            {children}
        </CrossFlowTouchpointContext.Provider>
    );
};

export const CrossFlowTouchpointEnabled = ({
    children,
    productKeys,
    screenName,
    viewedOnMount = true
}: PropsWithChildren<TouchpointOptions>) => {
    const { fireCrossFlowScreenEvent, fireUIEvent } = useFireEvent({
        productCohorts: getProductCohorts(productKeys),
        screenName
    });

    const onViewTouchpoint = useCallback(() => {
        if (screenName) {
            fireCrossFlowScreenEvent(screenName, {
                recommendedProductIds: productKeys
            });
            return;
        }
        fireUIEvent({
            action: "viewed",
            actionSubject: WAC_GENERIC_TOUCHPOINT_NAME
        });
    }, [screenName, fireUIEvent, fireCrossFlowScreenEvent, productKeys]);

    useEffect(() => {
        if (viewedOnMount) {
            onViewTouchpoint();
        }
    }, [onViewTouchpoint, viewedOnMount]);

    return (
        <CrossFlowTouchpointContext.Provider
            value={{
                isCrossFlowEnabled: true,
                productKeys,
                screenName,
                onViewTouchpoint
            }}
        >
            {children}
        </CrossFlowTouchpointContext.Provider>
    );
};

export const CrossFlowTouchpointProvider = ({
    children,
    productKeys,
    screenName,
    viewedOnMount = true
}: PropsWithChildren<TouchpointOptions>) => {
    const { isUserLoggedIn } = useLoggedInUser();
    const crossFlow = useCrossFlow();
    const { fireOperationalEvent } = useFireEvent({
        isUserLoggedIn,
        isCrossFlowEnabled: crossFlow?.isEnabled
    });

    if (!isUserLoggedIn || !crossFlow?.isEnabled) {
        fireOperationalEvent({
            action: "disabled",
            actionSubject: "crossFlowFrontend",
            eventType: "operational"
        });
        return (
            <CrossFlowTouchpointDisabled>
                {children}
            </CrossFlowTouchpointDisabled>
        );
    }
    return (
        <CrossFlowTouchpointEnabled
            productKeys={productKeys}
            screenName={screenName}
            viewedOnMount={viewedOnMount}
        >
            {children}
        </CrossFlowTouchpointEnabled>
    );
};

export const useCrossFlowTouchpointContext = () =>
    useContext(CrossFlowTouchpointContext);
